// Global HTTP interceptor:
//  1. Adds x-auth-secret and Authorization headers to backend requests.
//  2. Refreshes the access token once on 401 and retries.
//  3. Surfaces any non-2xx response from the backend as an antd toast,
//     using the BE-provided message/error field when available.
//
// Call sites that handle errors themselves (e.g. show bespoke copy or render
// inline form errors) should pass `skipGlobalErrorToast: true` in the fetch
// init to suppress the global toast.

import { API_BASE_URL } from './api'

/** Fire-and-forget toast via the ToastBridge component mounted inside <App>. */
function toast(text, type = 'error') {
  try {
    window.dispatchEvent(new CustomEvent('om_toast', { detail: { type, text } }))
  } catch {
    // jsdom / SSR fallback — swallow
  }
}

// Keep a reference to the original fetch
const originalFetch = globalThis.fetch?.bind(globalThis)

if (typeof originalFetch === 'function') {
  const base = String(API_BASE_URL || '').replace(/\/$/, '')
  const refreshUrl = `${base}/api/users/refresh-token`

  function resolveUrl(input) {
    try {
      if (typeof input === 'string') return input
      if (input && typeof input.url === 'string') return input.url // Request object
    } catch {
      // ignore
    }
    return ''
  }

  function shouldAttach(url) {
    try {
      if (!url) return false
      // Attach only if the request is going to our backend base URL
      return url.startsWith(base)
    } catch {
      return false
    }
  }

  function getSecret() {
    try {
      // Primary key: 'x-auth-secret'; fallback to 'authSecret' if present
      return (
        localStorage.getItem('x-auth-secret') ||
        localStorage.getItem('authSecret') ||
        ''
      )
    } catch {
      return ''
    }
  }

  function getTokens() {
    try {
      return {
        accessToken: localStorage.getItem('accessToken') || '',
        refreshToken: localStorage.getItem('refreshToken') || '',
      }
    } catch {
      return { accessToken: '', refreshToken: '' }
    }
  }

  function setTokens(tokens) {
    try {
      if (tokens.accessToken) localStorage.setItem('accessToken', tokens.accessToken)
      if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken)
    } catch { /* ignore */ }
  }

  function clearAuth() {
    try {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      // Optional: window.location.reload() or dispatch event
      window.dispatchEvent(new CustomEvent('om_unauthorized'))
    } catch { /* ignore */ }
  }

  async function tryRefresh() {
    const secret = getSecret()
    const { refreshToken } = getTokens()
    if (!refreshToken) return null
    try {
      const resp = await originalFetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(secret ? { 'x-auth-secret': secret } : {}),
        },
        body: JSON.stringify({ refreshToken }),
      })
      if (!resp.ok) return null
      const json = await resp.json().catch(() => null)
      const newAccess = json?.accessToken
      const newRefresh = json?.refreshToken
      if (typeof newAccess === 'string' && newAccess.length > 0) {
        setTokens({ accessToken: newAccess, refreshToken: newRefresh })
        return newAccess
      }
      return null
    } catch {
      return null
    }
  }

  async function doFetchOnce(input, init, attachHeaders = true) {
    const url = resolveUrl(input)
    const attach = shouldAttach(url)

    // Build a headers object without mutating the original
    const newInit = { ...(init || {}) }
    // Strip our custom keys so they don't end up on the request init
    delete newInit.skipGlobalErrorToast
    const headers = new Headers(init?.headers || (input && input.headers))

    if (attachHeaders && attach) {
      const secret = getSecret()
      if (secret) headers.set('x-auth-secret', secret)
      const { accessToken } = getTokens()
      if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    }

    newInit.headers = headers

    if (input instanceof Request) {
      // Recreate Request to ensure headers are applied
      const req = new Request(input, newInit)
      return originalFetch(req)
    }

    return originalFetch(url, newInit)
  }

  /** Pull a human-readable detail from a parsed JSON error body or string. */
  function extractDetail(body) {
    if (body == null) return null
    if (typeof body === 'string') {
      const trimmed = body.trim()
      return trimmed.length > 0 ? trimmed : null
    }
    if (typeof body !== 'object') return String(body)
    // Common Spring/REST shapes
    return (
      body.message ||
      body.error ||
      body.detail ||
      body.errorMessage ||
      (Array.isArray(body.errors) && body.errors.length > 0
        ? body.errors.map(e => (typeof e === 'string' ? e : e?.message || e?.error)).filter(Boolean).join('، ')
        : null)
    )
  }

  function buildErrorMessage(status, statusText, detail) {
    if (status === 0) return 'تعذر الاتصال بالخادم'
    const prefix = `حدث خطأ (${status})`
    const tail = detail || statusText
    return tail ? `${prefix}: ${tail}` : prefix
  }

  /** Read the body of a clone of the response and show a toast. Fire-and-forget. */
  async function showErrorToast(resp) {
    try {
      const clone = resp.clone()
      const ct = String(clone.headers.get('content-type') || '').toLowerCase()
      let body = null
      if (ct.includes('application/json')) {
        body = await clone.json().catch(() => null)
      } else {
        body = await clone.text().catch(() => null)
      }
      const detail = extractDetail(body)
      toast(buildErrorMessage(resp.status, resp.statusText, detail))
    } catch {
      // Last-ditch: status only
      toast(buildErrorMessage(resp.status, resp.statusText, null))
    }
  }

  globalThis.fetch = async function patchedFetch(input, init) {
    const skipToast = !!(init && init.skipGlobalErrorToast)
    const url = resolveUrl(input)
    const ourBackend = shouldAttach(url)
    const isRefreshCall = url === refreshUrl

    let resp
    try {
      resp = await doFetchOnce(input, init, true)
    } catch (networkErr) {
      // Network-level failure (DNS, offline, CORS preflight, etc.)
      if (ourBackend && !skipToast && !isRefreshCall) {
        toast(buildErrorMessage(0, null, networkErr?.message || 'فشل الاتصال'))
      }
      throw networkErr
    }

    // If unauthorized, try to refresh access token once and retry
    if (resp && resp.status === 401) {
      if (isRefreshCall) {
        // The refresh call itself returned 401 — don't loop, don't double-toast.
        return resp
      }
      const refreshed = await tryRefresh()
      if (refreshed) {
        // retry with new access token; this retry path will run through the
        // toast-on-error logic below just like a fresh call.
        resp = await doFetchOnce(input, init, true)
      } else {
        clearAuth()
        if (ourBackend && !skipToast) {
          toast('انتهت الجلسة، يرجى تسجيل الدخول مجدداً')
        }
        return resp
      }
    }

    if (resp && !resp.ok && ourBackend && !skipToast && !isRefreshCall) {
      // Don't await — the caller shouldn't block on toast rendering.
      void showErrorToast(resp)
    }

    return resp
  }
}
